'use client';
import { FunctionComponent, useCallback, useMemo } from 'react';
import { OccurrenceList } from '../OccurrenceList';
import {
    PublicCourse,
    PublicEducator,
    PublicOccurrence,
} from '@cademy-package/sdk-component-types';
import { useRouter } from 'next/navigation';
import styles from './styles.module.scss';
import { getBookingUrl } from '../../services/getBookingUrl';
import { useIsEmbed } from '../../contexts/AppContext/useIsEmbed';
import { useIsMarketplace } from '../../contexts/AppContext/useIsMarketplace';
import { isChildOccurrence } from '../../services/occurrences/isChildOccurrence';
import classNames from 'classnames';

const Title: FunctionComponent<{ visible: boolean }> = ({ visible }) => {
    if (!visible) {
        return null;
    }

    return (
        <h2 className={styles.occurrenceSectionHeader} id="occurrence-header">
            Dates
        </h2>
    );
};

export type OccurrenceSectionProps = {
    course: PublicCourse;
    educator: PublicEducator;
    occurrences: Array<PublicOccurrence>;
    showTitle?: boolean;
    className?: string;
};

export const OccurrenceSection: FunctionComponent<OccurrenceSectionProps> = ({
    course,
    educator,
    occurrences,
    showTitle = true,
    className,
}) => {
    const router = useRouter();
    const isEmbed: boolean = useIsEmbed();
    const isMarketplace: boolean = useIsMarketplace();
    const hidePricing = useMemo(
        () => !isMarketplace && educator.feature_flags?.hide_pricing,
        [isMarketplace, educator.feature_flags]
    );
    const onOccurrenceSelected = useCallback(
        (occurrence: PublicOccurrence) => {
            const bookingUrl: string = getBookingUrl({
                courseSlug: course.slug,
                courseType: course.type,
                educatorSlug: educator.slug,
                isEmbed,
                occurrenceId: occurrence._id,
            });
            if (isEmbed) {
                window.open(bookingUrl, '_blank');
            } else {
                router.push(bookingUrl);
            }
        },
        [course, educator, isEmbed, router]
    );

    const visibleOccurrences: Array<PublicOccurrence> = useMemo(
        () =>
            occurrences.filter((occurrence) => {
                if (!isChildOccurrence(occurrence)) {
                    return true;
                }

                if (occurrence.individually_bookable) {
                    return true;
                }
            }),
        [occurrences]
    );

    if (visibleOccurrences.length === 0) {
        return null;
    }

    if (['on-demand', 'on-request'].includes(course.type)) {
        return null;
    }

    return (
        <section
            className={classNames(styles.occurrenceSection, className)}
            aria-labelledby="occurrence-header"
        >
            <Title visible={showTitle} />
            <OccurrenceList
                course={course}
                educator={educator}
                occurrences={occurrences}
                occurrencesPerPage={5}
                onOccurrenceSelected={onOccurrenceSelected}
                hidePricing={hidePricing}
            />
        </section>
    );
};
